import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectGroupComponent } from '../list/project-group/project-group.component';
import { toProjectVms, sanitizeProjectDetails } from '../list/list-grouping.util';
import { ProjectService } from '../services/project.service';
import { AllBidsSubmittedDialogComponent } from '../allbids-submitted-dialog/allbids-submitted-dialog.component';
import { BidSubmittedDialogComponent } from '../bid-submitted-dialog/bid-submitted-dialog.component';
import { ModalService } from '../services/modal.service';
import { DeclineSuccessComponent } from '../decline-success/decline-success.component';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { AcceptedOfferedProposalDialogComponent } from '../accepted-offered-proposal-dialog/accepted-offered-proposal-dialog.component';
import { AtpSignAcceptedDialogComponent } from '../atp-sign-accepted-dialog/atp-sign-accepted-dialog.component';
import { AppService } from '../services/app.service';
import { scrollToProjectWhenReady } from '../helpers/scroll-to-project.util';
import { SignedAtpSiteVisit } from '../model/signed-atp-site-visit';
import { BatchDeclineSummary } from '../model/batch-decline';

@Component({
  standalone: true,
  selector: 'info-invitation',
  imports: [
    CommonModule,
    ProjectGroupComponent,
    AllBidsSubmittedDialogComponent,
    BidSubmittedDialogComponent,
    DeclineSuccessComponent,
    AcceptedOfferedProposalDialogComponent,
    AtpSignAcceptedDialogComponent,
  ],
  templateUrl: './invitations-page.component.html',
  styleUrl: './invitations-page.component.scss',
})
export class InvitationsPage implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  data: any = null;
  bidSubmittedActive: boolean = false;
  singleBidSubmittedActive: boolean = false;
  declineSuccessActive: boolean = false;
  declineBatch: BatchDeclineSummary | null = null;
  isOfferAcceptedActive: boolean = false;
  // Plurality of the accept that opened the confirmation, carried on the
  // ModalService emission. Previously hard-bound to `false` here, so a bulk
  // accept-all on this page confirmed "The offer was accepted".
  isOfferAcceptedMultiple: boolean = false;
  isAtpSignAccepted: boolean = false;
  // Site visits the last ATP signature on this page left schedulable, relayed
  // up from the product line that signed. Cleared when the confirmation
  // closes, so a later signature that opened nothing cannot show a stale
  // offer. Held on the page, never in a service — a root-provided subject
  // would keep the previous result across navigations.
  signedAtpSiteVisits: SignedAtpSiteVisit[] = [];
  atpModalHeaderTitle: string = 'ATP Signed & Project Active';
  atpModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';
  address: any = { streetAddress: '', cityStateZip: '' };
  loading: boolean = true;
  isFixedPrice: boolean = false;
  isAtp: boolean = false;
  // Set when the vendor arrived from the Dashboard work map's "View project"
  // (My Offers pin → /invitations, since My Offers is the INVITATION+OFFERED
  // set) — the project to scroll to + flash once the list renders.
  private scrollTargetProjectId: string | null = null;
  private destroyed = false;

  constructor(
    private projectService: ProjectService,
    private modalService: ModalService,
    private utilityService: CommonUtilityServiceService,
    private appService: AppService
  ) {}

  ngOnInit() {
    // Read the navigation state BEFORE fetchData(): the ProjectService cache is
    // a ReplaySubject, so a cached emission fires synchronously inside fetchData().
    this.scrollTargetProjectId =
      (window.history.state && window.history.state['scrollToProjectId']) ??
      null;
    this.fetchData();
    this.modalService.isSubmittedAllBidsModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => (this.bidSubmittedActive = isOpen));
    this.modalService.isSubmittedBidModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => (this.singleBidSubmittedActive = isOpen));
    this.modalService.isDeclineModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((detail) => {
        // CON-148 #6. This assignment MUST stay outside the guard. It used to be
        // `if (detail.isOpen) { this.declineSuccessActive = true; ... }`, which only ever wrote
        // `true`: closing the dialog emits `isOpen: false`, the guard skipped it, and the flag stayed
        // set. The second decline then emitted `true` onto a binding that was already `true`, Angular
        // saw no change, and the dialog never re-opened - until a tab switch destroyed the component
        // and reset the field, which is exactly the "only reappears on the Pending tab" symptom.
        // The field population below stays guarded: those values are only meaningful when opening.
        this.declineSuccessActive = detail.isOpen;
        if (detail.isOpen) {
          this.declineBatch = detail.batch ?? null;
          this.isFixedPrice = detail.isFixedPrice;
          this.isAtp = detail.isAtp;
          if (detail.siteDetail.site) {
            this.address = this.utilityService.formatAddress(
              detail.siteDetail
            );
          }
        }
      });
    this.modalService.isOfferAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((offer) => {
        this.isOfferAcceptedActive = offer.open;
        this.isOfferAcceptedMultiple = offer.multiple;
      });
    this.modalService.isAtpSignAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.isAtpSignAccepted = isOpen;
      });
    this.appService.setRedirectUrl('');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByProjectId(index: number, item: any): string {
    return item?.projectId ?? `${index}`;
  }

  toggleCloseSubmittedAllBidsModal() {
    this.modalService.toggleCloseSubmittedAllBidsModal();
  }

  toggleCloseSubmittedBidModal() {
    this.modalService.toggleCloseSubmittedBidModal();
  }

  toggleCloseDeclineSuccessModal() {
    this.modalService.toggleCloseDeclineSuccessModal();
  }

  toggleCloseOfferAcceptedModal() {
    this.modalService.toggleCloseOfferAcceptedModal();
  }

  toggleCloseAtpSignAcceptedModal() {
    this.signedAtpSiteVisits = [];
    this.modalService.toggleCloseAtpSignAcceptedModal();
  }

  fetchData() {
    this.projectService
      .fetchData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        const filtered = sanitizeProjectDetails(result)
          .map((parent: any) => ({
            ...parent,
            sites: parent.sites.filter(
              (child: any) =>
                child?.proposal?.status === 'INVITATION' ||
                child?.proposal?.status === 'OFFERED' ||
                // Task B4 — a countered proposal is still an open opportunity
                // (staffing hasn't resolved it). Omitting this drops the row
                // off this list the moment a counter is submitted, the exact
                // "vanishes off the page it belongs on" defect the design doc
                // calls out for APPROVED/Waiting-On-Others — the PA would lose
                // sight of their own outstanding ask while staffing works on
                // the answer. dashboard-kpi.util.ts's
                // INVITATION_STATUSES and work-map-scope.util.ts's
                // MY_OFFERS_STATUSES mirror this filter and must stay in sync.
                child?.proposal?.status === 'COUNTERED' ||
                // Staffing counter-back — staffing answered the counter with a
                // revised fee, so this row is a live offer the PA must accept,
                // counter once more, or decline. It belongs on this list for
                // the same reason OFFERED does.
                child?.proposal?.status === 'RE_OFFERED'
            ),
          }))
          .filter(
            (parent: any) => parent.sites.length > 0 && parent.projectId
          );
        // Group the flat project/site/proposal rows into the nested
        // Project → Site → Product view-model the CONNECT 2.0 cards render.
        this.data = toProjectVms(filtered);
        this.loading = false;

        if (this.scrollTargetProjectId) {
          const target = this.scrollTargetProjectId;
          this.scrollTargetProjectId = null; // one-shot — don't re-scroll on refresh
          scrollToProjectWhenReady(target, () => this.destroyed);
        }
      });
  }
}
